import React, { useCallback, useEffect, useRef, useState } from "react";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import { Box, Button, CircularProgress, Typography } from "@mui/material";
import { toPng } from "html-to-image";

/** Word Wheel Quest App Store / Play Store icon canvas. */
export const APP_ICON_SIZE = 512;

const SAVE_BAR_H = 64;
const PUBLIC = process.env.PUBLIC_URL || "";

/** Primary App Store / Play icon — from android-word-wheel-quest `icon.png`. */
export const WORD_WHEEL_APP_ICON_ART = `${PUBLIC}/ad/word-wheel-quest-app-icon-512.png`;

/**
 * Word Wheel Quest app icon capture — 512×512 edge-to-edge.
 */
export default function AppIconPage({
    art = WORD_WHEEL_APP_ICON_ART,
    fileName = "word-wheel-quest-app-icon-512x512.png",
    label = "App icon",
    screenshotFrame = "word-wheel-app-icon",
    children = null,
    captureBackground = "#1e1b4b",
}) {
    const [previewScale, setPreviewScale] = useState(1);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const frameRef = useRef(null);

    useEffect(() => {
        const updateScale = () => {
            const padding = 32;
            const availableW = window.innerWidth - padding;
            const availableH = window.innerHeight - SAVE_BAR_H - padding;
            setPreviewScale(Math.min(1, availableW / APP_ICON_SIZE, availableH / APP_ICON_SIZE));
        };
        updateScale();
        window.addEventListener("resize", updateScale);
        return () => window.removeEventListener("resize", updateScale);
    }, []);

    useEffect(() => {
        const prevHtml = document.documentElement.style.overflow;
        const prevBody = document.body.style.overflow;
        document.documentElement.style.overflow = "hidden";
        document.body.style.overflow = "hidden";
        return () => {
            document.documentElement.style.overflow = prevHtml;
            document.body.style.overflow = prevBody;
        };
    }, []);

    const handleDownloadPng = useCallback(async () => {
        const node = frameRef.current;
        if (!node || saving) return;
        setSaving(true);
        setSaveError(null);
        try {
            const dataUrl = await toPng(node, {
                width: APP_ICON_SIZE,
                height: APP_ICON_SIZE,
                pixelRatio: 1,
                cacheBust: true,
                backgroundColor: captureBackground,
            });
            const link = document.createElement("a");
            link.download = fileName;
            link.href = dataUrl;
            link.click();
        } catch (e) {
            setSaveError(e?.message || "Failed to download PNG");
        } finally {
            setSaving(false);
        }
    }, [captureBackground, fileName, saving]);

    return (
        <Box
            sx={{
                position: "fixed",
                inset: 0,
                display: "flex",
                flexDirection: "column",
                alignItems: "center",
                justifyContent: "center",
                bgcolor: "#0f172a",
                overflow: "hidden",
                zIndex: 1,
                gap: 1.5,
                pb: `${SAVE_BAR_H}px`,
                boxSizing: "border-box",
            }}
        >
            <Box
                sx={{
                    width: Math.round(APP_ICON_SIZE * previewScale),
                    height: Math.round(APP_ICON_SIZE * previewScale),
                    position: "relative",
                    overflow: "hidden",
                    flexShrink: 0,
                    boxShadow: "0 12px 40px rgba(0,0,0,0.55)",
                }}
            >
                <Box
                    ref={frameRef}
                    data-screenshot-frame={screenshotFrame}
                    sx={{
                        width: APP_ICON_SIZE,
                        height: APP_ICON_SIZE,
                        transform: `scale(${previewScale})`,
                        transformOrigin: "top left",
                        position: "relative",
                        overflow: "hidden",
                        bgcolor: captureBackground,
                        lineHeight: 0,
                    }}
                >
                    {children || (
                        <Box
                            component="img"
                            src={art}
                            alt="Word Wheel Quest app icon"
                            sx={{
                                position: "absolute",
                                inset: 0,
                                width: "100%",
                                height: "100%",
                                objectFit: "cover",
                                objectPosition: "center",
                                display: "block",
                            }}
                        />
                    )}
                </Box>
            </Box>

            <Box
                sx={{
                    position: "fixed",
                    left: 0,
                    right: 0,
                    bottom: 0,
                    minHeight: SAVE_BAR_H,
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    justifyContent: "center",
                    gap: 0.5,
                    py: 1,
                    bgcolor: "rgba(15, 23, 42, 0.96)",
                    borderTop: "1px solid rgba(148, 163, 184, 0.25)",
                    zIndex: 20,
                    px: 2,
                    boxSizing: "border-box",
                }}
            >
                <Typography variant="caption" sx={{ color: "rgba(226,232,240,0.75)" }}>
                    Word Wheel Quest · {label} · {APP_ICON_SIZE}×{APP_ICON_SIZE}
                </Typography>
                <Button
                    variant="contained"
                    size="medium"
                    startIcon={
                        saving ? <CircularProgress size={16} color="inherit" /> : <SaveAltIcon />
                    }
                    onClick={handleDownloadPng}
                    disabled={saving}
                    sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        bgcolor: "#38bdf8",
                        color: "#0c4a6e",
                        "&:hover": { bgcolor: "#7dd3fc" },
                    }}
                >
                    Download PNG
                </Button>
                {saveError ? (
                    <Typography variant="caption" color="error" sx={{ lineHeight: 1.2 }}>
                        {saveError}
                    </Typography>
                ) : null}
            </Box>
        </Box>
    );
}
